import { Box, Flex, Spinner } from "@chakra-ui/react";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";
import { StyledTextareaContainer } from "./style";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import { CHAT_STATUS_GENERAL, ROUTE_NAMES } from "@root/constants";
import useProjectsDashboard from "@root/hooks/useProjectsDashboard";
import FileDropdown from "../checklist/Filesdropdown";
import { FileSelectMenu } from "./UploadDashboad.tsx/fileselection button";
import { UploadCircleIcon } from "@root/assets/icons/upload-icon";
import PauseIcon from "@root/assets/icons/pause-icon";
import ChatbotDropdown from "@root/pages/dashboard/components/chatbot-dropdown/chatbot-dropdown";
import { OutsideCloseWrapper } from "./UploadDashboad.tsx/outside-close-wrapper";
import { useProjectsContext } from "@root/context/projects-context";
import { useRouteDetection } from "@root/hooks/useRouteDetection";
import { usePlaceholderText } from "@root/hooks/usePlaceholderText";
import { useTextareaHeight } from "@root/hooks/useTextareaHeight";
import { useFileHandling } from "@root/hooks/useFileHandling";
import { useDocumentPolling } from "@root/hooks/useDocumentPolling";
import { useDocumentFetch } from "@root/hooks/useDocumentFetch";
import { useChatSubmit } from "@root/hooks/useChatSubmit";
import { useStopChat } from "@root/hooks/useStopChat";
import { useKeyboardNavigation } from "@root/hooks/useKeyboardNavigation";
import { AttachmentFilesList } from "./components/AttachmentFilesList";
import { DROPDOWN_LEGAL_ITEMS, getProjectDropdownItems } from "./config/dropdownConfig";

export const DashBoardTextarea = ({
  onChangehandler,
  input,
  onSend,
  fileIndex,
  noOfDocs,
  name = "",
  onChatbotDropdownMenuClose,
  onProjectChatbotDropdownMenuClose,
}: any) => {
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [showFileMenuChecklist, setShowFileMenuChecklist] = useState(false);
  const [displayFiles, setDisplayFiles] = useState<any[]>([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachStatus, setAttachStatus] = useState<
    null | "success" | "error" | "processing"
  >(null);

  const {
    setFlagValidateUrl,
    attachedFile,
    setAttachedFile,
    selectedProject,
    currentPathname,
    documentsData,
    fetchAllDocs,
    currentMessageResponse,
    currentThread,
    setIsTypewriterActive,
    attachedFileProject,
    isPolling,
    setInput,
  } = useMainChatContext();

  const { onSubmitHandler } = useProjectsDashboard();
  const { fetchDocument, documentData } = useProjectsContext();
  const { t } = useTranslation("common");

  const completedDocs = documentsData?.rows;

  // Route detection
  const {
    pathname,
    isLegalChatbot,
    isMultiDoc,
    isKcs,
    isDrawingAnalysis,
    isProjectDocument,
  } = useRouteDetection();

  // Textarea height management
  const { inputRef, inputHeight, handleInput } = useTextareaHeight();

  // Placeholder text
  const placeholderText = usePlaceholderText({
    currentMessageStatus: currentMessageResponse?.status,
    selectedProject,
  });

  // File handling
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const { pendingFiles, handleFileChange, removeAttachment } = useFileHandling({
    attachedFile,
    setAttachedFile,
    displayFiles,
    setDisplayFiles,
    isDrawingAnalysis,
    isLegalChatbot,
    fetchDocument,
    setInput,
    input,
  });

  // Document polling
  useDocumentPolling({
    attachedFile,
    setAttachStatus,
  });

  // Document fetch
  useDocumentFetch({
    isMultiDoc,
    isLegalChatbot,
    attachedFile,
    fetchAllDocs,
  });

  // Derived state
  const isPending =
    CHAT_STATUS_GENERAL.PENDING === currentMessageResponse?.status ||
    CHAT_STATUS_GENERAL.IN_PROGRESS.includes(currentMessageResponse?.status);

  const isLegalChatWithFile =
    (currentPathname.startsWith(ROUTE_NAMES.LEGAL_CHATBOT) ||
      currentPathname.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN)) &&
    attachedFile?.length > 0;

  const isDrawingAnalysisWithFile =
    (currentPathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD &&
      attachedFile?.length > 0) ||
    (currentPathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT) &&
      documentData?.id);

  const isSendDisabled =
    pendingFiles?.length > 0 ||
    isPolling ||
    (input?.trim()?.length === 0 &&
      !isLegalChatWithFile &&
      !isDrawingAnalysisWithFile) ||
    (currentPathname.includes(ROUTE_NAMES.CHECKLIST) &&
      attachedFile?.length === 0);

  // Chat submit
  const { onSubmit } = useChatSubmit({
    input,
    inputRef,
    isSendDisabled,
    attachedFile,
    currentPathname,
    setDisplayFiles,
  });

  // Stop chat
  const { handleStopChat } = useStopChat({
    currentMessageResponse,
    currentThread,
    setIsTypewriterActive,
  });

  // Keyboard navigation
  const { onKeyPressHandler } = useKeyboardNavigation({
    fileIndex,
    noOfDocs,
    inputRef,
    onSubmit: () => onSubmit(onSend),
  });

  // Handlers
  const handleUploadClick = () => {
    fileUploadRef.current?.click();
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Adjust height on change
    const target = e.target as HTMLTextAreaElement;
    if (target) {
      if (target.value.trim().length === 0) {
        target.style.height = "50px";
      } else {
        target.style.height = "auto";
        target.style.height = `${target.scrollHeight}px`;
      }
    }
    setFlagValidateUrl(true);
    onChangehandler(e);
  };

  function handleSendClick(): void {
    if (isProjectDocument && selectedProject) {
      setShowFileMenu(true);
    } else if (isMultiDoc) {
      setShowFileMenuChecklist(true);
    }
  }

  // Calculate border radius
  const borderRadius =
    displayFiles?.length > 0 ||
      pendingFiles?.length > 0 ||
      attachedFileProject?.length > 0
      ? "20px"
      : input.length === 0
        ? "38px"
        : inputHeight > 69
          ? "20px"
          : "28px";

  // Calculate alignment
  const alignItems =
    input.length === 0
      ? "flex-start"
      : inputHeight > 69
        ? "flex-end"
        : "center";

  const DROPDOWN_PROJECT_ITEMS = getProjectDropdownItems(isDrawingAnalysis);

  return (
    <Flex
      bg="theme.bg.box"
      zIndex={1}
      position="relative"
      overflow="visible"
      borderRadius={borderRadius}
      px="12px"
      py={displayFiles?.length > 0 ? "12px" : "8px"}
      boxShadow="0px 4px 4px #15161A1A"
      flexDirection="column"
      gap="8px"
      width="100%"
      alignItems={alignItems}
    >
      {/* File attachments list */}
      <AttachmentFilesList
        files={displayFiles}
        pendingFiles={pendingFiles}
        onRemove={removeAttachment}
        pathname={pathname}
        showForDocumentIntegration={true}
        attachLoading={attachLoading}
        attachedFile={attachedFile}
      />

      {/* Textarea + Icons */}
      <Flex
        width="100%"
        position="relative"
        alignItems={inputHeight > 69 ? "flex-end" : "center"}
      >
        <StyledTextareaContainer
          ref={inputRef}
          borderRadius={
            input.length === 0 ? "28px" : inputHeight > 69 ? "20px" : "28px"
          }
          onChange={isPolling ? () => { } : onChangeHandler}
          value={input}
          onKeyDown={isPolling ? () => { } : onKeyPressHandler}
          name={name}
          placeholder={placeholderText}
          minH="50px"
          pr="70px"
          width="100%"
          pointerEvents={isPolling ? "none" : "auto"}
          onInput={handleInput}
        />

        {/* LEFT: Chatbot dropdowns */}
        {(isKcs || isLegalChatbot) && (
          <Box position="absolute" left="1px" zIndex={10}>
            <ChatbotDropdown
              items={DROPDOWN_LEGAL_ITEMS}
              onUploadClick={handleUploadClick}
              onMenuClose={onChatbotDropdownMenuClose}
            />
            <input
              type="file"
              accept=".pdf, .docx"
              ref={fileUploadRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              multiple={isMultiDoc}
            />
          </Box>
        )}

        {isProjectDocument && !selectedProject && (
          <Box position="absolute" left="1px" zIndex={10}>
            <Box
              data-tour-item="project-chatbot-dropdown"
              display="inline-block"
            >
              <ChatbotDropdown
                items={DROPDOWN_PROJECT_ITEMS}
                onMenuClose={onProjectChatbotDropdownMenuClose}
              />
            </Box>
          </Box>
        )}

        {((isProjectDocument && selectedProject) ||
          isMultiDoc ||
          isDrawingAnalysis) && (
            <Box position="absolute" left="1px" zIndex={10}>
              <Box
                data-tour-item={
                  isDrawingAnalysis || isMultiDoc
                    ? "project-chatbot-dropdown"
                    : undefined
                }
                display="inline-block"
              >
                <ChatbotDropdown
                  items={DROPDOWN_PROJECT_ITEMS}
                  onUploadClick={
                    isDrawingAnalysis ? handleUploadClick : handleSendClick
                  }
                  onMenuClose={onProjectChatbotDropdownMenuClose}
                />
              </Box>
              <input
                type="file"
                ref={fileUploadRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                multiple={isMultiDoc}
                accept="application/pdf"
              />
            </Box>
          )}

        {/* File selection menus */}
        {showFileMenu && (
          <Box position="absolute" left="40px" zIndex={10}>
            <FileSelectMenu
              onChange={(file: any) =>
                setAttachedFile((prev: any[]) =>
                  prev.some((f) => f.id === file.id) ? prev : [...prev, file]
                )
              }
              onClose={() => setShowFileMenu(false)}
            />
          </Box>
        )}

        {showFileMenuChecklist && (
          <OutsideCloseWrapper onClose={() => setShowFileMenuChecklist(false)}>
            <Box position="absolute" left="40px" zIndex={10}>
              <FileDropdown
                files={completedDocs}
                selectedFiles={
                  pathname.startsWith(ROUTE_NAMES.CHECKLIST_CHAT)
                    ? attachedFile
                    : displayFiles
                }
                onChange={(files: any) => {
                  setAttachedFile(files);
                  setDisplayFiles(files);
                }}
                isMultiFiles={isMultiDoc}
                isLegalChatbot={isLegalChatbot}
                openUpward={pathname.startsWith(ROUTE_NAMES.CHECKLIST_CHAT)}
              />
            </Box>
          </OutsideCloseWrapper>
        )}

        {/* RIGHT: Send/Pause button */}
        <Flex
          position="absolute"
          right="0"
          gap="16px"
          zIndex={10}
          alignItems={"center"}
        >
          {currentMessageResponse?.status === CHAT_STATUS_GENERAL.STREAMING ? (
            <Box cursor="pointer" onClick={handleStopChat}>
              <PauseIcon />
            </Box>
          ) : (
            <CommonTooltip label={t("tooltip.sendQuery")}>
              <Box
                opacity={isSendDisabled ? 0.5 : 1}
                cursor={isSendDisabled ? "not-allowed" : "pointer"}
                onClick={() => onSubmit(onSend)}
              >
                {isPending ? (
                  <Box
                    bg="#FFFFFF"
                    borderRadius="full"
                    p="6px"
                    width="32px"
                    height="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Spinner color="#5fdce0ff" />
                  </Box>
                ) : (
                  <UploadCircleIcon
                    bg={isSendDisabled ? "#F5F5F5" : "black"}
                    color={isSendDisabled ? "#595959" : "white"}
                  />
                )}
              </Box>
            </CommonTooltip>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
